/**
 * Shared time-window filtering for timeline views.
 *
 * Used by BOTH the executive dashboard timeline (ExecDashboardV2) and the
 * trip timeline (Timeline2) so the Today / Tomorrow / Upcoming / Range
 * semantics never drift between the two views.
 *
 * Timezone handling
 * -----------------
 * Component datetimes come in two flavors:
 *  - Offset-carrying ISO strings ("2026-07-25T12:50:00+02:00" / "...Z"),
 *    e.g. FlightAware-tracked actual/estimated arrivals. These parse to the
 *    exact absolute instant, so "is this in the past?" is computed correctly
 *    regardless of the viewer's timezone (the cards still DISPLAY the
 *    location-local wall time via the *PreserveTimezone formatters).
 *  - Naive strings ("2026-07-25T12:50:00") composed from parsed
 *    confirmations. These are location-local wall times with no stored
 *    timezone, so the best available interpretation is the viewer's local
 *    timezone (JavaScript's spec behavior for offset-less date-times).
 *
 * getComponentFilterEndTime therefore prefers offset-carrying sources
 * (tracked arrivals) over naive ones wherever both exist.
 */

/** Grace periods (hours) an item stays visible under "Upcoming" after it ends. */
export const UPCOMING_GRACE_HOURS = {
  flight: 2,       // after landing
  rental_car: 2,   // after drop-off
  default: 1,      // after end time (meetings, events, hotels, chauffeur, ...)
};

/**
 * Parse a datetime value to a Date instant.
 * - Offset-carrying ISO strings -> exact instant.
 * - Naive local strings -> interpreted in the viewer's local timezone.
 * - Bare dates ("2026-07-25") -> NOTE: the JS spec parses date-only strings
 *   as UTC midnight; normalize them to local midnight so day-based filters
 *   (today/tomorrow/range) behave as users expect.
 * Returns null for falsy or unparseable input.
 */
export function parseEventInstant(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const str = String(value).trim();
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

const addHours = (value, hours) => {
  const d = parseEventInstant(value);
  return d ? new Date(d.getTime() + hours * 60 * 60 * 1000) : null;
};

/** First parseable candidate wins (skips bare times like "12:50"). */
const firstInstant = (...candidates) => {
  for (const c of candidates) {
    const d = parseEventInstant(c);
    if (d) return d;
  }
  return null;
};

/**
 * Best-effort start datetime for a timeline item (string or null).
 * Shared with the exec dashboard's timeline sort.
 */
export const getComponentStartDatetime = (component) => {
  if (component.start_datetime) return component.start_datetime;
  const cd = component.component_data || component;
  if (cd.start_datetime) return cd.start_datetime;
  if (cd.departure_datetime) return cd.departure_datetime;
  if (cd.check_in_date) return cd.check_in_date;
  if (cd.pickup_datetime) return cd.pickup_datetime;
  if (cd.meeting_date) return cd.meeting_date;
  if (cd.event_date) return cd.event_date;
  const legs = cd.flight_legs || cd.legs || [];
  if (legs.length > 0 && legs[0].departure_datetime) return legs[0].departure_datetime;
  if (legs.length > 0 && legs[0].departure_time) return legs[0].departure_time;
  return null;
};

/**
 * The instant after which an item no longer counts as "still relevant" for
 * the Upcoming filter: its real end time plus a type-specific grace period.
 */
export const getComponentFilterEndTime = (component) => {
  const cd = component.component_data || {};
  const type = component.component_type;

  // Flight legs: 2h after landing. Prefer tracked arrival times - they carry
  // explicit UTC offsets, so the comparison is timezone-exact.
  if (component.isFlightLeg || type === 'commercial_flight' || type === 'private_flight' || type === 'flight') {
    const leg = component.flightLeg || {};
    const arrival = firstInstant(
      leg.actual_arrival,
      leg.estimated_arrival,
      component.end_datetime,
      leg.arrival_datetime,
      leg.arrival_date && leg.arrival_time ? `${leg.arrival_date}T${leg.arrival_time}` : null,
      leg.arrival_time
    );
    if (arrival) return new Date(arrival.getTime() + UPCOMING_GRACE_HOURS.flight * 3600 * 1000);
    return addHours(getComponentStartDatetime(component), UPCOMING_GRACE_HOURS.flight);
  }

  // Rental cars (whole component or exploded pickup/return events): 2h after drop-off
  if (type === 'rental_car' || type === 'car_rental' || component.isRentalCarEvent) {
    const dropoff = firstInstant(
      cd.dropoff_datetime,
      cd.dropoff_date && cd.dropoff_time ? `${cd.dropoff_date}T${cd.dropoff_time}` : null,
      cd.dropoff_date ? `${cd.dropoff_date}T23:59:59` : null,
      component.isRentalCarEvent ? component.start_datetime : null
    );
    if (dropoff) return new Date(dropoff.getTime() + UPCOMING_GRACE_HOURS.rental_car * 3600 * 1000);
    return addHours(cd.pickup_datetime || getComponentStartDatetime(component), UPCOMING_GRACE_HOURS.rental_car);
  }

  // Meetings / speaker programs / events: 1h after the end time
  if (type === 'meeting' || type === 'speaker_meeting' || type === 'event') {
    const end = firstInstant(
      cd.meeting_end_time && cd.meeting_date ? `${cd.meeting_date}T${cd.meeting_end_time}` : null,
      component.end_datetime,
      cd.end_datetime,
      cd.meeting_datetime,
      cd.meeting_date,
      component.start_datetime
    );
    return end ? new Date(end.getTime() + UPCOMING_GRACE_HOURS.default * 3600 * 1000) : null;
  }

  // Hotels: the stay is relevant until 1h after checkout. Whole components
  // use the check-out date/time; exploded check-in/check-out events each get
  // 1h past their own event time (the check-out event keeps the stay visible).
  if (type === 'hotel' && !component.isHotelEvent) {
    const checkout = firstInstant(
      cd.check_out && cd.check_out_time ? `${cd.check_out}T${cd.check_out_time}` : null,
      cd.check_out ? `${cd.check_out}T23:59:59` : null,
      component.end_datetime
    );
    if (checkout) return new Date(checkout.getTime() + UPCOMING_GRACE_HOURS.default * 3600 * 1000);
  }

  // Everything else (hotel events, chauffeur, memos, ...): 1h after the end
  // time, falling back to the start time.
  const end = firstInstant(component.end_datetime, getComponentStartDatetime(component));
  return end ? new Date(end.getTime() + UPCOMING_GRACE_HOURS.default * 3600 * 1000) : null;
};

/**
 * Time-window predicate shared by both timelines.
 *
 * @param {object} component - flattened timeline item
 * @param {string} filter - 'all' | 'today' | 'tomorrow' | 'upcoming' | 'range'
 * @param {object} [options]
 * @param {Date}   [options.now] - injectable clock (tests)
 * @param {object} [options.range] - { from: Date|null, to: Date|null } for 'range'
 * @returns {boolean} true if the component should be shown
 */
export function passesTimeFilter(component, filter, { now = new Date(), range = null } = {}) {
  if (!filter || filter === 'all') return true;

  const startTime = parseEventInstant(getComponentStartDatetime(component));
  const filterEndTime = getComponentFilterEndTime(component);

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowEnd = new Date(todayEnd.getTime() + 24 * 60 * 60 * 1000);

  if (filter === 'today') {
    if (!startTime) return false;
    return startTime >= todayStart && startTime < todayEnd;
  }

  if (filter === 'tomorrow') {
    if (!startTime) return false;
    return startTime >= todayEnd && startTime < tomorrowEnd;
  }

  if (filter === 'upcoming') {
    // Undated (TBD) items stay visible - hiding them would silently lose
    // components that still need scheduling.
    if (!startTime && !filterEndTime) return true;
    if (startTime && startTime > now) return true;
    if (filterEndTime && filterEndTime > now) return true;
    return false;
  }

  if (filter === 'range') {
    const { from, to } = range || {};
    if (!from && !to) return true; // no range chosen: don't hide anything
    if (!startTime && !filterEndTime) return false;

    // Range bounds: start-of-day .. end-of-day (inclusive)
    const rangeStart = from ? new Date(from.getFullYear(), from.getMonth(), from.getDate()) : null;
    const rangeEnd = to ? new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1) : null;

    // The component occupies [start, filterEnd]; include it if that interval
    // overlaps the chosen window.
    const compStart = startTime || filterEndTime;
    const compEnd = filterEndTime || startTime;
    if (!compStart || !compEnd) return false;

    if (rangeStart && compEnd < rangeStart) return false;
    if (rangeEnd && compStart >= rangeEnd) return false;
    return true;
  }

  return true;
}


/**
 * Executive scoping for the executive dashboard timeline.
 *
 * The dashboard lists every trip the selected executive is on, but a trip can
 * be shared (a spouse, a colleague, a family booking). Each component is
 * therefore scoped on its own: it is shown only when it is assigned to the
 * selected executive.
 *
 * Assignment, in order of trust:
 *   1. Explicit assignees: component_data.assigned_executives (manual form,
 *      bookings, parsed emails), hotel room_details[].guest_ids, and
 *      passengers[].executive_id matched during parsing.
 *   2. Only when none of those exist: component.executive_id /
 *      component_data.executive_id. That field records which executive's
 *      inbox the item came through or who filed it, so on a shared trip it
 *      often names the EA's primary executive rather than the traveller.
 *      Treating it as an assignee put Yoav's flights on Jeremy's dashboard.
 *
 * A component that names no executive at all (legacy data, or a confirmation
 * for someone the parser could not match) has nothing to scope on, so it keeps
 * the trip-level behaviour and is shown to every executive on the trip.
 */
const addId = (set, value) => {
  if (value === null || value === undefined || value === '') return;
  set.add(String(value));
};

/** Executives explicitly assigned to the component (see note above). */
export const getComponentExecutiveIds = (component) => {
  const cd = (component && component.component_data) || {};
  const ids = new Set();
  (Array.isArray(cd.assigned_executives) ? cd.assigned_executives : []).forEach((id) => addId(ids, id));
  (Array.isArray(cd.room_details) ? cd.room_details : []).forEach((room) => {
    (Array.isArray(room?.guest_ids) ? room.guest_ids : []).forEach((id) => addId(ids, id));
  });
  [cd.passengers, cd.additional_travelers].forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((p) => addId(ids, p && p.executive_id));
  });
  return Array.from(ids);
};

/** Fallback owner when nothing explicit exists: the filing / inbox executive. */
export const getComponentFallbackExecutiveIds = (component) => {
  const cd = (component && component.component_data) || {};
  const ids = new Set();
  addId(ids, component && component.executive_id);
  addId(ids, cd.executive_id);
  return Array.from(ids);
};

export const componentAssignedToExecutive = (component, executiveId) => {
  if (!executiveId) return true;
  const explicit = getComponentExecutiveIds(component);
  if (explicit.length > 0) return explicit.includes(String(executiveId));
  const fallback = getComponentFallbackExecutiveIds(component);
  if (fallback.length > 0) return fallback.includes(String(executiveId));
  return true; // nothing to scope on; see note above
};
